import type {
  FachgruppeDetails,
  FachgruppeList,
  FachgruppeMember,
  MemberStatus,
  Permission,
  StoredFachgruppe,
} from '@asas/domain';

import type { FehlerBefund, FeldBefund } from '../daten/fehler';
import { datumAnzeige } from '../mitarbeiter/formular';

/**
 * Die Fachgruppenansicht als reine Logik (AC-104) — ohne React, ohne Netz.
 *
 * Wer wann zu einer Fachgruppe gehoert, rechnet NICHT diese Datei, sondern der
 * Server ueber `@asas/domain/membership`. Hier wird nur angezeigt und die
 * Serverantwort in den Zustand uebernommen.
 */

export const MITGLIED_STATUS_LABELS: Readonly<Record<MemberStatus, string>> = {
  beschaeftigt: 'beschäftigt',
  eintritt_geplant: 'Eintritt geplant',
};

/** „seit 15.03.2024" — oder „seit jeher" fuer Altbestand ohne Eintrittsdatum. */
export function seitAnzeige(member: FachgruppeMember): string {
  return member.since === null ? 'seit jeher' : `seit ${datumAnzeige(member.since)}`;
}

/**
 * „bis 28.06.2026" bei einem geplanten Wechsel in eine andere Fachgruppe, sonst
 * „—". Der Austritt steht hier bewusst nicht: er ist ein Personaldatum
 * (`employee:manage`), die Zugehoerigkeit ein Organisationsdatum.
 */
export function bisAnzeige(member: FachgruppeMember): string {
  return member.until === null ? '—' : `bis ${datumAnzeige(member.until)}`;
}

/**
 * Uebernimmt eine gespeicherte Fachgruppe in die Liste (AC-016, AK-2: es gilt
 * die SERVERANTWORT). Eine neue Fachgruppe hat noch niemanden; eine bekannte
 * behaelt ihre Zahl, denn Anlegen und Umbenennen aendern keine Zuordnung.
 */
export function mitGespeichert(
  liste: FachgruppeList,
  gespeichert: StoredFachgruppe,
): FachgruppeList {
  const bekannt = liste.fachgruppen.some((fachgruppe) => fachgruppe.id === gespeichert.id);
  const fachgruppen = bekannt
    ? liste.fachgruppen.map((fachgruppe) =>
        fachgruppe.id === gespeichert.id ? { ...fachgruppe, ...gespeichert } : fachgruppe,
      )
    : [...liste.fachgruppen, { ...gespeichert, memberCount: 0 }];

  return { ...liste, fachgruppen };
}

/** Dasselbe fuer die geoeffnete Fachgruppe: Name und Version, die Besetzung bleibt. */
export function offenMitGespeichert(
  offen: FachgruppeDetails,
  gespeichert: StoredFachgruppe,
): FachgruppeDetails {
  return offen.id === gespeichert.id
    ? { ...offen, name: gespeichert.name, version: gespeichert.version }
    : offen;
}

/**
 * Teilt Feldbefunde der Antwort auf: der zum Namen ans Feld, alle anderen in
 * die Meldung. Anlegen und Umbenennen haben nur dieses eine Feld.
 */
export function namensMeldungen(felder: readonly FeldBefund[]): {
  readonly name: string | null;
  readonly allgemein: readonly string[];
} {
  const name = felder.find((befund) => befund.path === 'name')?.message ?? null;
  const allgemein = felder
    .filter((befund) => befund.path !== 'name')
    .map((befund) => befund.message);

  return { name, allgemein };
}

/**
 * Prueft einen Namensentwurf aus dem Browserspeicher — veraenderbarer Text,
 * kein Vertrauen (§20.2 Punkt 6). Ob der Name gueltig ist, sagt der Server.
 */
export function pruefeNamensEntwurf(roh: unknown): string | null {
  return typeof roh === 'string' ? roh : null;
}

/**
 * Sperrt ein gescheiterter LESEVERSUCH weitere? Nur eine Ablehnung (403): jeder
 * weitere Versuch schriebe eine unloeschbare Audit-Zeile (§19, §20.2 Punkt 3).
 * Ein Eingabefehler (etwa ein Stichtag in der Zukunft) sperrt nicht — er ist
 * mit einer anderen Eingabe behoben.
 */
export function sperrtWeitereVersuche(befund: FehlerBefund | null): boolean {
  return befund?.art === 'zugriff';
}

/**
 * Bietet die Oberflaeche Anlegen und Umbenennen an? Nur mit `fachgruppe:manage`
 * — dem Recht, das `fachgruppen.create` und `fachgruppen.rename` verlangen
 * (`FachgruppenAnsicht.spec.tsx` haelt die Zuordnung fest). Ohne das Recht
 * schriebe schon der erste Klick eine unloeschbare Audit-Zeile (§19). Die
 * Sperre nach einem 403 bleibt als Rueckfall, falls sich Rechte waehrend der
 * Sitzung aendern.
 */
export const FACHGRUPPEN_PFLEGERECHT: Permission = 'fachgruppe:manage';

export function darfFachgruppenPflegen(rechte: ReadonlySet<Permission>): boolean {
  return rechte.has(FACHGRUPPEN_PFLEGERECHT);
}
